import { User } from "@prisma/client";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import {
  Icon24Award,
  Icon24Carousel,
  Icon24Cash,
  Icon24Chat,
  Icon24Check,
  Icon24CheckOutline,
  Icon24Clock,
  Icon24Earth,
  Icon24Education,
  Icon24Grid,
  Icon24Patent,
  Icon24Pen,
  Icon24PeopleB,
  Icon24Presentation,
  Icon24Suit,
  Icon24Trash,
  Icon44ScientificWork,
} from "immigram-ui-cmon/src/components/Icons";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { TFunction } from "next-i18next";
import React, { memo, ReactNode, useContext } from "react";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin } from "@/lib/auth/rbac";
import { ApplicationContext } from "@/lib/contexts/applicationContext";
import { ExperienceType } from "@/lib/experienceType";

interface ExperienceCardProps {
  ownUser: User;
  id?: string;
  applicationSectionStatus: ApplicationSectionStatusType | null;
  experienceType: ExperienceType;
  children: ReactNode;
  rootClassName?: string;
  innerClassName?: string;
  onExpand: () => void;
  onDelete?: () => void;
  error?: string;
  fromYear?: string;
  toYear?: string;
  subIds?: string[];
  isCompleted?: boolean;
}

export const getExperienceIconByType = (experienceType: ExperienceType) => {
  switch (experienceType) {
    case ExperienceType.ScientificWorkOrPhD: {
      return <Icon44ScientificWork />;
    }
    case ExperienceType.PublicTalks: {
      return <Icon24Presentation />;
    }
    case ExperienceType.Entrepreneurship: {
      return <Icon24Cash />;
    }
    case ExperienceType.Employment: {
      return <Icon24Suit />;
    }
    case ExperienceType.Articles: {
      return <Icon24Pen />;
    }
    case ExperienceType.MentionInMedia: {
      return <Icon24Pen />;
    }
    case ExperienceType.Contests: {
      return <Icon24PeopleB />;
    }
    case ExperienceType.Award: {
      return <Icon24Award />;
    }
    case ExperienceType.CommunityDevelopment: {
      return <Icon24Earth />;
    }
    case ExperienceType.TeachingAndMentorship: {
      return <Icon24Education />;
    }
    case ExperienceType.OpenSourceContribution: {
      return <Icon24Grid />;
    }
    case ExperienceType.Patent: {
      return <Icon24Patent />;
    }
    case ExperienceType.AnotherExperience: {
      return <Icon24Carousel />;
    }
  }
};

export const getExperienceTextByType = (
  experienceType: ExperienceType,
  t: TFunction
) => {
  switch (experienceType) {
    case ExperienceType.ScientificWorkOrPhD: {
      return t("application:experienceType.heading.scientificWorkOrPhD");
    }
    case ExperienceType.PublicTalks: {
      return t("application:experienceType.heading.publicTalks");
    }
    case ExperienceType.Entrepreneurship: {
      return t("application:experienceType.heading.entrepreneurship");
    }
    case ExperienceType.Employment: {
      return t("application:experienceType.heading.employment");
    }
    case ExperienceType.Articles: {
      return t("application:experienceType.heading.articles");
    }
    case ExperienceType.MentionInMedia: {
      return t("application:experienceType.heading.mentionInMedia");
    }
    case ExperienceType.Contests: {
      return t("application:experienceType.heading.contests");
    }
    case ExperienceType.Award: {
      return t("application:experienceType.heading.award");
    }
    case ExperienceType.CommunityDevelopment: {
      return t("application:experienceType.heading.communityDevelopment");
    }
    case ExperienceType.TeachingAndMentorship: {
      return t("application:experienceType.heading.teachingAndMentorship");
    }
    case ExperienceType.OpenSourceContribution: {
      return t("application:experienceType.heading.openSourceContribution");
    }
    case ExperienceType.Patent: {
      return t("application:experienceType.heading.patent");
    }
    case ExperienceType.AnotherExperience: {
      return t("application:experienceType.heading.anotherExperience");
    }
  }
};

const ExperienceCard = (props: ExperienceCardProps) => {
  const { comments } = useContext(ApplicationContext);
  let unresolvedComments = comments.filter(
    (item) =>
      item.subEntityId === props.id &&
      !item.isResolved &&
      (isAdmin(props.ownUser) || (!isAdmin(props.ownUser) && !item.isDraft))
  );

  if (Array.isArray(props.subIds)) {
    unresolvedComments = unresolvedComments.filter((item) =>
      item.name.split("_").length === 3
        ? props.subIds?.includes(item.name.split("_")[1])
        : true
    );
  }

  const allSectionCommentsCount = comments.filter(
    (item) => item.subEntityId === props.id
  ).length;
  const unresolvedCount = unresolvedComments.length;
  const experienceIcon = getExperienceIconByType(props.experienceType);

  const isCompleted =
    props.isCompleted ??
    props.applicationSectionStatus ===
      ApplicationSectionStatusType.ReviewCompleted;

  return (
    <>
      {props.fromYear && (
        <Badge
          className="absolute z-20 mt-[-10px] ml-4"
          size="150"
          type={BadgeType.Neutral}
        >
          {!props.toYear || props.toYear === props.fromYear
            ? `${props.fromYear}`
            : `${props.fromYear} - ${
                +props.toYear === 2099 ? "Now" : props.toYear
              }`}
        </Badge>
      )}
      <div
        className={clsxm(
          "group relative z-10 w-full cursor-pointer overflow-hidden rounded-2xl transition-all",
          isCompleted
            ? "bg-green-50"
            : "border border-gray-150 shadow-sm hover:border-gray-200",
          props.rootClassName
        )}
      >
        <div
          className={clsxm(
            "flex h-full min-h-[80px] flex-col justify-center py-4 pl-4 pr-14 transition-all",
            props.applicationSectionStatus ===
              ApplicationSectionStatusType.SubmittedForReview &&
              !isCompleted &&
              "group-hover:pr-24",
            props.innerClassName
          )}
          onClick={props.onExpand}
        >
          {props.children}
        </div>

        <>
          {(!isCompleted || isAdmin(props.ownUser)) && (
            <div className="group absolute top-1/2 right-14 z-10 -mt-3.5 flex h-8 w-8 items-center justify-center rounded-full pr-1">
              {/* Delete icon */}
              {props.onDelete !== undefined &&
                props.applicationSectionStatus ===
                  ApplicationSectionStatusType.SubmittedForReview && (
                  <div
                    className="absolute text-black-900 opacity-0 transition group-hover:opacity-100"
                    onClick={props.onDelete}
                  >
                    <Icon24Trash />
                  </div>
                )}
            </div>
          )}

          <div
            className={clsxm(
              "group absolute top-1/2 right-4 z-10 -mt-4 flex h-8 w-8 items-center justify-center rounded-full",
              !(
                props.onDelete !== undefined &&
                props.applicationSectionStatus !==
                  ApplicationSectionStatusType.SubmittedForReview &&
                (!isCompleted || isAdmin(props.ownUser))
              ) && "pointer-events-none"
            )}
          >
            {/* Delete icon */}
            {((unresolvedCount === 0 && !isCompleted) ||
              isAdmin(props.ownUser)) &&
              props.onDelete !== undefined &&
              props.applicationSectionStatus !==
                ApplicationSectionStatusType.SubmittedForReview && (
                <div
                  className={clsxm(
                    "absolute mr-20 mb-2 text-black-900 opacity-0 transition group-hover:opacity-100",
                    !experienceIcon && "mr-0"
                  )}
                  onClick={props.onDelete}
                >
                  <Button
                    size="200"
                    type={ButtonType.Flat}
                    icon={<Icon24Trash />}
                  />
                </div>
              )}

            {/* Experience type icons */}
            {experienceIcon &&
              unresolvedCount === 0 &&
              props.applicationSectionStatus !==
                ApplicationSectionStatusType.SubmittedForReview &&
              !isCompleted && (
                <div
                  className={clsxm(
                    "pointer-events-none absolute text-black-600 opacity-100 transition",
                    props.error && "text-blood-600",
                    props.onDelete !== undefined &&
                      "group-hover:pointer-events-none"
                  )}
                >
                  {allSectionCommentsCount ? (
                    <div>
                      {isAdmin(props.ownUser) ? (
                        <Icon24CheckOutline />
                      ) : (
                        <Icon24Clock />
                      )}
                    </div>
                  ) : (
                    experienceIcon
                  )}
                </div>
              )}
          </div>

          {/* Status icons: reviewing (without comments), reviewing (has comments), succeeded */}
          <div
            className={clsxm(
              !isCompleted && "opacity-100 transition group-hover:opacity-100"
            )}
          >
            {(props.applicationSectionStatus ===
              ApplicationSectionStatusType.SubmittedForReview &&
              !isCompleted) ||
            unresolvedCount > 0 ? (
              <div
                className={clsxm(
                  "absolute top-1/2 right-4 -mt-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs text-black-900",
                  unresolvedCount > 0 && "bg-yellow-600 text-white-900",
                  allSectionCommentsCount &&
                    unresolvedCount === 0 &&
                    "bg-white-100"
                )}
              >
                {unresolvedCount > 0 ? (
                  <Icon24Chat />
                ) : allSectionCommentsCount ? (
                  <>
                    {isAdmin(props.ownUser) ? (
                      <Icon24CheckOutline />
                    ) : (
                      <Icon24Clock />
                    )}
                  </>
                ) : (
                  <Icon24Clock />
                )}
              </div>
            ) : isCompleted ? (
              <div
                className={clsxm(
                  "pointer-events-none absolute top-1/2 right-4 -mt-4 flex h-8 w-8 items-center justify-center rounded-full bg-green-600 text-xs text-white-900"
                )}
              >
                <Icon24Check />
              </div>
            ) : null}
          </div>
        </>
      </div>
    </>
  );
};

export default memo(ExperienceCard);
